import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyDto } from 'dtos'
import { ClusterShipperDto, clusterShipperValidationSchema } from 'dtos/clusterShipperDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useState } from 'react'
import { useDebounce } from 'utils'

interface ClusterShippersAddEditDialogProps {
  initialValues: ClusterShipperDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: ClusterShipperDto) => void
}

export default function ClusterShippersAddEditDialog({
  initialValues,
  isLoading,
  open,
  onClose,
  onSave
}: ClusterShippersAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingClusterConversionCompanies: false,
    isGettingExporters: false,
    isGettingBuyers: false
  })

  const [clusterConversionCompanies, setClusterConversionCompanies] = useState<CompanyDto[]>([])
  const [exporters, setExporters] = useState<CompanyDto[]>([])
  const [buyers, setBuyers] = useState<CompanyDto[]>([])

  const getCompaniesAutocompleteBase = (search: string, statuses: string[]) =>
    controllers.getCompanies({
      junction: Junction.AND,
      order: 'asc',
      orderBy: 'name',
      page: 0,
      pageSize: AUTOCOMPLETE_PAGESIZE,
      search,
      statuses
    })

  const getClusterConversionCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingClusterConversionCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isClusterConversionCompany'])
      .then(response => {
        setClusterConversionCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingClusterConversionCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getExportersAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingExporters', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isExporterCompany'])
      .then(response => {
        setExporters(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingExporters', false)
      })
  }, DEBOUNCE_DELAY)

  const getBuyersAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingBuyers', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isBuyerCompany'])
      .then(response => {
        setBuyers(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingBuyers', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={clusterShipperValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.clusterShipperGuid ? 'Edit' : 'Add'} Cluster Shipper</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='Cluster Shipper #' value={values.clusterShipperNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={clusterConversionCompany => clusterConversionCompany.name!}
                      noOptionsText={
                        loadingState.isGettingClusterConversionCompanies
                          ? 'Searching Cluster Conversion Companies...'
                          : 'No Cluster Conversion Companies. Start typing to search for Cluster Conversion Companies.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('clusterConversionCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getClusterConversionCompaniesAutocomplete(value)
                      }}
                      options={clusterConversionCompanies.map(clusterConversionCompany => ({
                        companyGuid: clusterConversionCompany.companyGuid,
                        name: clusterConversionCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.clusterConversionCompany && errors.clusterConversionCompany)}
                          helperText={touched.clusterConversionCompany && errors.clusterConversionCompany}
                          label='Cluster Conversion Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingClusterConversionCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.clusterConversionCompany
                          ? {
                              companyGuid: values.clusterConversionCompany.companyGuid,
                              name: values.clusterConversionCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={exporter => exporter.name!}
                      noOptionsText={
                        loadingState.isGettingExporters
                          ? 'Searching Exporters...'
                          : 'No Exporters. Start typing to search for Exporters.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('exporterCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getExportersAutocomplete(value)
                      }}
                      options={exporters.map(exporter => ({
                        companyGuid: exporter.companyGuid,
                        name: exporter.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.exporterCompany && errors.exporterCompany)}
                          helperText={touched.exporterCompany && errors.exporterCompany}
                          label='Exporter'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingExporters && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.exporterCompany
                          ? {
                              companyGuid: values.exporterCompany.companyGuid,
                              name: values.exporterCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={buyer => buyer.name!}
                      noOptionsText={
                        loadingState.isGettingBuyers ? 'Searching Buyers...' : 'No Buyers. Start typing to search for Buyers.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('buyerCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getBuyersAutocomplete(value)
                      }}
                      options={buyers.map(buyer => ({
                        companyGuid: buyer.companyGuid,
                        name: buyer.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.buyerCompany && errors.buyerCompany)}
                          helperText={touched.buyerCompany && errors.buyerCompany}
                          label='Buyer'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingBuyers && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.buyerCompany
                          ? {
                              companyGuid: values.buyerCompany.companyGuid,
                              name: values.buyerCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <GreenText variant='outlined'>
                    {`**Cluster Shipper Number Label:**

A read-only label auto-generated when a Cluster Shipper is initially created.

If adding a new Cluster Shipper, the label displays a “-”.

If editing a Cluster Shipper, the label displays the existing Cluster Shipper Number.

**Cluster Conversion Company Drop-Down:**

A drop-down field that lists all active Cluster Conversion Companies in the system.

**Exporter Drop-down:**

A drop-down field that lists all active Exporter Companies in the system.

**Buyer Drop-down:**

A drop-down field that lists all active Buyer Companies in the system.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                  </GreenText>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
